import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDebounce } from './useDebounce';
import { createBodySampleForGoogle, defaultObjectField } from '@src/utils';
import { useGetPlacesSearchLocationMutation } from '@store/rtk/places';
import {
  getLatLngFromSuggestions,
  hasValidValues,
  parseLatLon,
} from '@utils/helpers';
import { logger } from '@utils/logger';

const validFields = ['address1', 'city', 'state', 'zipCode'];

const parseAddress = (address, placesData, LatLon) => {
  const checkLatLng = hasValidValues(LatLon);
  const coordinates = getLatLngFromSuggestions(placesData);
  let parsedAddress = {
    ...(checkLatLng && { ...LatLon }),
    ...(coordinates && { ...coordinates }),
    street: address,
  };

  return Object.fromEntries(
    Object.entries(parsedAddress).filter(([_, value]) => value)
  );
};

export const usePlacesSearch = (defaultValue: string = '') => {
  const [toggle, setToggle] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>(defaultValue);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [memberPlaces, setMemberPlaces] = useState<any[]>([]);
  const [getPlacesSearchLocation] = useGetPlacesSearchLocationMutation();

  useEffect(() => {
    setSearchQuery(defaultValue);
  }, [defaultValue]);

  const searchPlaces = useCallback(
    async (query: string) => {
      if (!query.trim() || query.length < 3) {
        setSuggestions([]);
        setMemberPlaces([]);
        return;
      }

      const { Place } = (await google.maps.importLibrary(
        'places'
      )) as google.maps.PlacesLibrary;

      const request = {
        textQuery: query,
        fields: [
          'displayName',
          'location',
          'nationalPhoneNumber',
          'formattedAddress',
          'googleMapsURI',
          'types',
          'svgIconMaskURI',
          'primaryType',
        ],
        language: 'en-US',
        region: 'us',
      };

      const { places } = await Place.searchByText(request);
      const newPlacesSearchLocation = JSON.parse(JSON.stringify(places));

      if (newPlacesSearchLocation && newPlacesSearchLocation.length > 0) {
        setToggle(true);
        setSuggestions(newPlacesSearchLocation || []);
        const optimizedArray = [
          ...new Set(newPlacesSearchLocation.flatMap((el) => el.types)),
        ];
        const bodyRequestSample = createBodySampleForGoogle(
          validFields,
          optimizedArray,
          query
        );
        const newFields = defaultObjectField({ ...bodyRequestSample }, query);
        const LatLon = parseLatLon(newFields.address1);
        const address = parseAddress(
          // TODO check this logic in the future, now sending query like "street: query"!
          // newFields.address1,
          query,
          newPlacesSearchLocation,
          LatLon
        );
        getPlacesSearchLocation({ data: address })
          .unwrap()
          .then((data) => {
            if (data) {
              setMemberPlaces(data?.locations);
            }
          });
      } else {
        try {
          getPlacesSearchLocation({ data: { street: query } })
            .unwrap()
            .then((data) => {
              if (data) {
                setToggle(true);
                setMemberPlaces(data?.locations);
                if (!places?.length) {
                  setSuggestions([]);
                }
              }
            });
        } catch (error) {
          logger.log({ error });
          setToggle(true);
          setSuggestions([]);
        }
      }
    },
    [getPlacesSearchLocation]
  );

  const debouncedSearch = useDebounce(searchPlaces, 1000);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      setMemberPlaces([]);
    }
    debouncedSearch(query);
  };

  return {
    searchQuery,
    setSearchQuery,
    suggestions,
    memberPlaces,
    handleSearchChange,
    toggle,
    setToggle,
  };
};
